'use client'

import type { Options, OptionsStackingValue } from 'highcharts'

import { formatters } from '@morpho-org/ui-v6/utils'
import { memo, useMemo } from 'react'

import type { ChartBaseProps } from '../types/chart'

import {
  createTooltipFormatter,
  createTooltipFormatterAllocationChart,
  createTooltipFormatterCumulativeChart,
  mergeOptions
} from '../helpers/highcharts'
import { useChartTheme } from '../hooks/useChartTheme'
import { useHighchartsOptions } from '../hooks/useHighchartsOptions'
import { Chart } from './Chart'

export interface CumulativeChartProps extends ChartBaseProps {
  stackingMode?: OptionsStackingValue
  withLegend?: boolean
  withTooltipAbsoluteValues?: boolean
  tooltipMode?: 'default' | 'allocation'
  assetSymbol?: string
  additionalOptions?: Options
  noAnimation?: boolean
}

export function CumulativeChart({
  stackingMode = 'normal',
  series,
  unit,
  withLegend = false,
  withTooltipAbsoluteValues = false,
  tooltipMode,
  assetSymbol,
  additionalOptions,
  noAnimation = false,
  ...props
}: CumulativeChartProps) {
  const { theme, themeMode } = useChartTheme()

  const { options: baseOptions } = useHighchartsOptions()

  const options: Options = useMemo(() => {
    const yFormatter = (value?: number | string) => {
      return formatters.short.unit(unit).digits(2).default('-').of(Number(value))
    }

    const tooltipFormatter =
      tooltipMode === 'allocation'
        ? createTooltipFormatterAllocationChart({
            theme,
            themeMode,
            stackingMode: stackingMode ?? 'normal',
            assetSymbol
          })
        : withTooltipAbsoluteValues
          ? createTooltipFormatter({
              formatters: {
                y: point => `${point.series.name}: ${yFormatter(point.y)}`,
                showSeriesColor: true,
                total: value => `${yFormatter(value)}`
              },
              theme,
              themeMode
            })
          : createTooltipFormatterCumulativeChart({ theme, themeMode })

    const isPercent = stackingMode === 'percent'

    return mergeOptions(
      baseOptions,
      {
        xAxis: {
          type: 'datetime'
        },
        yAxis: isPercent
          ? {
              min: 0,
              max: 115,
              tickPositions: [0, 25, 50, 75, 100],
              tickInterval: undefined,
              maxPadding: 0,
              endOnTick: false,
              showFirstLabel: false,
              showLastLabel: true,
              labels: {
                format: '{value}%',
                formatter: undefined
              }
            }
          : {
              min: undefined,
              max: undefined,
              tickPositions: undefined,
              tickInterval: undefined,
              endOnTick: true,
              showFirstLabel: false,
              showLastLabel: false,
              softMin: 0,
              maxPadding: 0.2,
              labels: {
                format: undefined,
                formatter: function () {
                  return yFormatter(this.value)
                }
              }
            },
        tooltip: {
          formatter: tooltipFormatter,
          outside: tooltipMode === 'allocation'
        },
        plotOptions: {
          areaspline: {
            stacking: stackingMode,
            lineWidth: 1
          },
          area: {
            lineWidth: 1
          },
          spline: {
            lineWidth: 1
          },
          series: {
            animation: !noAnimation,
            lineWidth: 1,
            states: {
              hover: {
                lineWidthPlus: 0,
                halo: { size: 0 }
              }
            }
          }
        },
        legend: {
          enabled: withLegend
        },
        series
      },
      additionalOptions ?? {}
    )
  }, [
    baseOptions,
    theme,
    themeMode,
    series,
    stackingMode,
    unit,
    withLegend,
    withTooltipAbsoluteValues,
    tooltipMode,
    assetSymbol,
    additionalOptions,
    noAnimation
  ])

  return <Chart options={options} {...props} />
}

export default memo(CumulativeChart)
